import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { nextSort, type SortState } from '../ui/SortableHeader';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import type { ActualsResponse, EvidenceScopeType, GroupActuals, GroupKpiValue, KpiCatalogueEntry, KpiValue, OpenEvidence, PersonActuals } from '../../types/strategicKpis';
import { CATEGORY_LABEL, CATEGORY_ORDER, STATUS_LABEL, formatAchievement, formatKpiValue, judgedOnYtdShare, kpiLabel, LIGHT_DOT_CLASS, noDataText, trafficLight } from '../../utils/strategicKpiFormat';
import { hasBabRows, paceAtYearEnd } from '../../utils/strategicKpiSelectors';
import type { SkpiFamily } from '../../utils/strategicKpiUrl';

export type Hierarchy = 'company-team' | 'team';
export type CellMode = 'actual' | 'achievement' | 'target' | 'prior';

export const CELL_MODE_LABEL: Record<CellMode, string> = {
    actual: 'Actual',
    achievement: 'On course (pace %)',
    target: 'Target',
    prior: 'Prior year',
};

/** The short names of the cell-mode segment. */
export const CELL_MODE_SHORT: Record<CellMode, string> = {
    actual: 'Actual',
    achievement: 'On course',
    target: 'Target',
    prior: 'Prior year',
};

/**
 * Props. `fam` and `cellMode` are optional: left out, the table keeps the choice itself (first family
 * present, "On course"). Given together with their callback, the page owns the choice (URL `fam`, `cell`).
 * Given without the callback, the value is fixed and the matching segment is not drawn.
 */
interface Props {
    data: ActualsResponse;
    /** Every KPI the matrix may show; the table narrows it to ONE family (E18). */
    kpis: KpiCatalogueEntry[];
    hierarchy: Hierarchy;
    /** Persons without any stored target become one line per node instead of rows of zeros. */
    hidePersonsWithoutTargets: boolean;
    onOpenEvidence: OpenEvidence;
    fam?: SkpiFamily | null;
    onFamChange?: (fam: SkpiFamily) => void;
    cellMode?: CellMode;
    onCellModeChange?: (mode: CellMode) => void;
    /** px from the viewport top to the bottom gutter, for the one table of the tab (`--tbl-offset`). */
    tblOffset?: number;
}

/** E18: the families that have at least one KPI in `kpis`, in catalogue order. A pure derivation from the payload. */
export function matrixFamilies(kpis: Pick<KpiCatalogueEntry, 'category'>[]): SkpiFamily[] {
    const present = new Set(kpis.map(k => k.category));
    return CATEGORY_ORDER.filter(c => present.has(c)) as SkpiFamily[];
}

/** The chosen family if the KPIs have it, else the first one that exists (never more than one family). */
export function resolveFamily(kpis: Pick<KpiCatalogueEntry, 'category'>[], wanted: SkpiFamily | null | undefined): SkpiFamily | null {
    const available = matrixFamilies(kpis);
    return wanted && available.includes(wanted) ? wanted : available[0] ?? null;
}

type Row =
    | { kind: 'group'; id: string; parentId: string | null; depth: number; group: GroupActuals; personsHere: number; spans: string[] | null; hasChildren: boolean }
    | { kind: 'person'; id: string; parentId: string; depth: number; person: PersonActuals }
    | { kind: 'sum' | 'gtarget'; id: string; parentId: string; depth: number; group: GroupActuals }
    | { kind: 'hidden'; id: string; parentId: string; depth: number; names: string[] };

const NAME_KEY = 'name';
const NAME_WIDTH = 280;
const KPI_WIDTH = 120;

/** Sticky first column: opaque, so the figures scroll under it. */
function nameCellStyle(row: Row, indent: number): CSSProperties {
    return { position: 'sticky', left: 0, zIndex: 1, background: rowBackground(row), paddingLeft: 8 + indent * 18, borderRight: '1px solid var(--hair)' };
}

function isTopRow(row: Row): boolean {
    return row.kind === 'group' && (row.group.scopeType === 'TOTAL' || row.group.scopeType === 'BU' || row.group.scopeType === 'SELECTION');
}

function rowBackground(row: Row): string {
    if (isTopRow(row)) return 'var(--surface-sunken)';
    if (row.kind === 'group' && row.group.scopeType === 'COMPANY') return 'var(--ground)';
    return 'var(--surface)';
}

/**
 * BU → Company → Team → Person (or BU → Team → Person), one column per KPI of ONE family, a value and a
 * small status dot per cell (calm: no coloured cell backgrounds). Where a group target is stored, the
 * open node carries two extra rows: "Σ persons" (the engine's personsActual / personsTarget /
 * achievementVsPersons) and "Group target" (the stored group target and the achievement against
 * it) — interview decision 1, side by side, never merged; without a group target the node row
 * already is the Σ-persons view. Persons without any target collapse into one line per node.
 *
 * E18: the family (CRM, Visits, Gross Profit, BAB, Market Data) and the cell mode are segments above the
 * table; the old chip "Steered" (all families at once) is gone. BAB has figures on companies and BUs
 * only, so that family shows just those rows. The table is the one `.tbl-wrap` of the tab (Q5).
 *
 * A team is a set of people reporting to one manager and may span companies (Alberici's direct
 * reports sit in dch, dit, dsp and dtr). Under a company, a team row lists only the members of
 * THAT company, but its figures are the team's pooled figures — the engine pools per team, and
 * the page does not re-pool (a ratio cannot be rebuilt from person ratios). Such a row says so
 * with a "spans …" badge; the "Team" hierarchy avoids the split altogether.
 *
 * Sorting works among siblings: persons within their team, teams within their company, and so
 * on. Σ / group-target rows stay at the bottom of their node.
 *
 * K3 (selection): the engine's SELECTION node is the root (in place of the BU / TOTAL row); under
 * it the BUs (several BUs) or directly the companies / teams, all over the selected persons. A
 * node the selection cuts is `partial` — its figures are over the selected persons and it has no
 * group target; its row says "n of m selected".
 */
export function DrillDownTable({ data, kpis, hierarchy, hidePersonsWithoutTargets, onOpenEvidence, fam, onFamChange, cellMode, onCellModeChange, tblOffset }: Props) {
    const [sort, setSort] = useState<SortState<string>>({ key: NAME_KEY, direction: 'asc' });
    const [expanded, setExpanded] = useState<Set<string>>(() => defaultExpanded(data));
    const [collapsedByUser, setCollapsedByUser] = useState(false);
    const [famLocal, setFamLocal] = useState<SkpiFamily | null>(null);
    const [cellLocal, setCellLocal] = useState<CellMode>('achievement');

    const families = useMemo(() => matrixFamilies(kpis), [kpis]);
    const family = resolveFamily(kpis, fam ?? famLocal);
    const famKpis = useMemo(() => (family ? kpis.filter(k => k.category === family) : []), [kpis, family]);
    const babOnly = family === 'bab';
    const cell: CellMode = cellMode ?? cellLocal;
    const famInteractive = !!onFamChange || fam === undefined;
    const cellInteractive = !!onCellModeChange || cellMode === undefined;

    const pickFam = (f: SkpiFamily) => { setFamLocal(f); onFamChange?.(f); };
    const pickCell = (m: CellMode) => { setCellLocal(m); onCellModeChange?.(m); };

    // A new BU / year / hierarchy resets the open nodes to the top level.
    const selectionRoot = data.groups.find(g => g.scopeType === 'SELECTION');
    const resetKey = `${data.bus.join(',')}|${data.year}|${hierarchy}|${selectionRoot?.scopeKey ?? ''}`;
    const [lastResetKey, setLastResetKey] = useState(resetKey);
    if (lastResetKey !== resetKey) {
        setLastResetKey(resetKey);
        setExpanded(defaultExpanded(data));
        setCollapsedByUser(false);
    }

    // ALL KPIs: the width store drops unknown keys, so a hidden family must not lose its widths.
    const allColumns = useMemo(() => [
        { key: NAME_KEY, width: NAME_WIDTH },
        ...kpis.map(k => ({ key: k.kpiKey, width: KPI_WIDTH })),
    ], [kpis]);
    const columns = useMemo(() => [
        { key: NAME_KEY, width: NAME_WIDTH },
        ...famKpis.map(k => ({ key: k.kpiKey, width: KPI_WIDTH })),
    ], [famKpis]);

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(allColumns, 'strategic-kpis-drilldown-col-widths');

    // A sort on a column of another family falls back to the name.
    const activeSort: SortState<string> = sort.key !== NAME_KEY && !famKpis.some(k => k.kpiKey === sort.key) ? { key: NAME_KEY, direction: 'asc' } : sort;

    const tree = useMemo(() => buildTree(data, hierarchy, hidePersonsWithoutTargets, babOnly), [data, hierarchy, hidePersonsWithoutTargets, babOnly]);

    const rows = useMemo(() => flatten(tree, expanded, activeSort, cell), [tree, expanded, activeSort, cell]);
    // ND-03: Market Data has no light before the year end — its cells show the value without a dot.
    const yearEnd = useMemo(() => new Set(famKpis.filter(k => paceAtYearEnd(k, data.windows.yearFraction)).map(k => k.kpiKey)), [famKpis, data.windows.yearFraction]);

    const onSort = (key: string) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, key !== NAME_KEY));
    };

    const toggle = useCallback((id: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const expandAll = () => { setExpanded(new Set(allGroupIds(tree))); setCollapsedByUser(false); };
    const collapseAll = () => { setExpanded(new Set()); setCollapsedByUser(true); };

    const wrapStyle = tblOffset !== undefined ? ({ ['--tbl-offset' as string]: `${tblOffset}px` } as CSSProperties) : undefined;
    const tableWidth = columns.reduce((s, c) => s + widthOf(c.key), 0);

    return (
        <div data-testid="drilldown-matrix" data-fam={family ?? ''}>
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                {famInteractive && families.length > 0 && (
                    <div className="seg" role="group" aria-label="KPI family">
                        {families.map(f => (
                            <button key={f} type="button" aria-pressed={f === family} onClick={() => pickFam(f)}>{CATEGORY_LABEL[f] ?? f}</button>
                        ))}
                    </div>
                )}
                {cellInteractive && (
                    <div className="seg" role="group" aria-label="Cell shows">
                        {(Object.keys(CELL_MODE_SHORT) as CellMode[]).map(m => (
                            <button key={m} type="button" aria-pressed={m === cell} title={CELL_MODE_LABEL[m]} onClick={() => pickCell(m)}>{CELL_MODE_SHORT[m]}</button>
                        ))}
                    </div>
                )}
                <span className="ml-auto flex shrink-0 gap-1">
                    <button type="button" onClick={expandAll} className="btn quiet sm">Expand all</button>
                    <button type="button" onClick={collapseAll} className="btn quiet sm">Collapse all</button>
                </span>
            </div>
            <p className="mb-2" style={{ fontSize: 'var(--fs-small)', lineHeight: 'var(--lh-small)', color: 'var(--text-muted)' }}>
                {data.persons.length} persons · {data.groups.filter(g => g.scopeType === 'TEAM').length} teams ·
                {' '}{data.groups.filter(g => g.scopeType === 'COMPANY').length} companies · cell = {CELL_MODE_LABEL[cell]}
                {' '}· dot = on track ≥ 100 %, close ≥ 90 %, behind below (vs the group target where one is stored, else vs Σ person targets) · no dot = no target{yearEnd.size > 0 ? ' or judged at the year end (Market Data)' : ''}{babOnly ? ' · BAB has figures per company and BU only' : ''} · click a figure for its rows
            </p>

            {/* The one table of the tab: its own height down to the viewport edge, header sticky inside it. */}
            <div className="tbl-wrap compact" style={wrapStyle}>
                <table className="tbl tbl-fixed" style={{ width: tableWidth }}>
                    <colgroup>
                        {columns.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                    </colgroup>
                    <thead>
                        <tr>
                            <MatrixHeader
                                label={babOnly ? 'BU › Company' : hierarchy === 'team' ? 'BU › Team › Person' : 'BU › Company › Team › Person'}
                                sortKey={NAME_KEY}
                                sort={activeSort}
                                onSort={onSort}
                                first
                                onResizeStart={x => startResize(NAME_KEY, x)}
                                onResetWidth={() => resetWidth(NAME_KEY)}
                                isResizing={resizingKey === NAME_KEY}
                            />
                            {famKpis.map(k => (
                                <MatrixHeader
                                    key={k.kpiKey}
                                    label={kpiLabel(k)}
                                    sortKey={k.kpiKey}
                                    sort={activeSort}
                                    onSort={onSort}
                                    numeric
                                    onResizeStart={x => startResize(k.kpiKey, x)}
                                    onResetWidth={() => resetWidth(k.kpiKey)}
                                    isResizing={resizingKey === k.kpiKey}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={columns.length} style={{ height: 96, textAlign: 'center', color: 'var(--text-muted)' }}>{collapsedByUser ? 'Everything is collapsed.' : 'No persons registered for this BU and year.'}</td></tr>
                        )}
                        {rows.map(r => (
                            <TableRow
                                key={r.id}
                                row={r}
                                kpis={famKpis}
                                cellMode={cell}
                                yearEnd={yearEnd}
                                isOpen={r.kind === 'group' && expanded.has(r.id)}
                                onToggle={toggle}
                                onOpenEvidence={onOpenEvidence}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------------------------

const KEY_STEP = 10;
const KEY_STEP_LARGE = 50;

/** Sortable, resizable header cell in kit classes (`.sort`, `.col-resize`); same behaviour as SortableHeader. */
function MatrixHeader({ label, sortKey, sort, onSort, numeric = false, first = false, onResizeStart, onResetWidth, isResizing }: {
    label: string; sortKey: string; sort: SortState<string>; onSort: (key: string) => void;
    numeric?: boolean; first?: boolean;
    onResizeStart: (clientX: number) => void; onResetWidth: () => void; isResizing: boolean;
}) {
    const active = sort.key === sortKey;
    const onKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const step = e.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
            const delta = e.key === 'ArrowRight' ? step : -step;
            onResizeStart(0);
            setTimeout(() => {
                window.dispatchEvent(new MouseEvent('mousemove', { clientX: delta }));
                window.dispatchEvent(new MouseEvent('mouseup'));
            }, 0);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onResetWidth();
        }
    };
    return (
        <th
            className={numeric ? 'n' : undefined}
            style={first ? { left: 0, zIndex: 3, borderRight: '1px solid var(--hair)' } : undefined}
            aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <button type="button" className="sort" title={label} onClick={() => onSort(sortKey)}>
                <span className="truncate">{label}</span>
                <span className="caret" aria-hidden="true">{active ? (sort.direction === 'asc' ? '▲' : '▼') : ''}</span>
            </button>
            <span
                role="separator"
                aria-orientation="vertical"
                aria-label={`Resize ${label}`}
                tabIndex={0}
                className="col-resize"
                style={isResizing ? { background: 'var(--action)', opacity: 0.35 } : undefined}
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onResizeStart(e.clientX); }}
                onClick={e => e.stopPropagation()}
                onDoubleClick={e => { e.stopPropagation(); onResetWidth(); }}
                onKeyDown={onKeyDown}
                title="Drag to resize · double-click or Enter to reset · arrow keys when focused"
            />
        </th>
    );
}

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

const BADGE: CSSProperties = { marginLeft: 4, padding: '0 6px', borderRadius: 4, fontSize: 'var(--fs-small)', lineHeight: 'var(--lh-small)', fontWeight: 450, flexShrink: 0, background: 'var(--surface-sunken)', color: 'var(--text-muted)' };
const BADGE_ATTN: CSSProperties = { ...BADGE, background: 'var(--attention-tint)', color: 'var(--attention-ink)' };
const SMALL_MUTED: CSSProperties = { fontSize: 'var(--fs-small)', lineHeight: 'var(--lh-small)', color: 'var(--text-muted)', flexShrink: 0 };

function TableRow({ row, kpis, cellMode, yearEnd, isOpen, onToggle, onOpenEvidence }: {
    row: Row; kpis: KpiCatalogueEntry[]; cellMode: CellMode; yearEnd: Set<string>; isOpen: boolean;
    onToggle: (id: string) => void;
    onOpenEvidence: OpenEvidence;
}) {
    const scope = rowScope(row);
    const bg = rowBackground(row);

    if (row.kind === 'hidden') {
        const hiddenStyle: CSSProperties = Object.assign(nameCellStyle(row, row.depth), { fontStyle: 'italic', color: 'var(--text-muted)', fontSize: 'var(--fs-small)' });
        return (
            <tr data-row-kind="hidden">
                <td style={hiddenStyle}>
                    <span className="inline-flex max-w-full items-center gap-1">
                        <ChevronRight size={12} className="shrink-0" aria-hidden="true" />
                        <span className="truncate" title={`No person target stored for this year: ${row.names.join(', ')}`}>{row.names.length} without targets: {row.names.join(', ')}</span>
                    </span>
                </td>
                {kpis.map(k => <td key={k.kpiKey} style={{ background: bg }} />)}
            </tr>
        );
    }

    const derived = row.kind === 'sum' || row.kind === 'gtarget';
    const weight = isTopRow(row) ? 640 : row.kind === 'group' ? 560 : undefined;
    const rowStyle: CSSProperties = { background: bg, fontWeight: weight, ...(derived ? { fontStyle: 'italic', color: 'var(--text-muted)', fontSize: 'var(--fs-label)' } : null) };

    return (
        <tr style={rowStyle} data-row-kind={row.kind} data-scope={scope ? `${scope.scopeType}:${scope.scopeKey}` : undefined}>
            <td style={nameCellStyle(row, row.depth)}>
                {row.kind === 'group' && (row.hasChildren ? (
                    <button
                        type="button"
                        onClick={() => onToggle(row.id)}
                        className="sort"
                        style={{ width: '100%', gap: 4, justifyContent: 'flex-start', textAlign: 'left' }}
                        title={isOpen ? 'Collapse' : 'Expand'}
                        aria-expanded={isOpen}
                    >
                        {isOpen ? <ChevronDown size={14} className="shrink-0" aria-hidden="true" /> : <ChevronRight size={14} className="shrink-0" aria-hidden="true" />}
                        <GroupLabel row={row} />
                    </button>
                ) : (
                    <span className="inline-flex max-w-full items-center gap-1" style={{ paddingLeft: 18 }}><GroupLabel row={row} /></span>
                ))}
                {row.kind === 'person' && (
                    <span className="inline-flex max-w-full items-center gap-1">
                        <span className="truncate" title={`${row.person.fullName} · ${row.person.companyCode} · StaffID ${row.person.staffId}${row.person.hubspotOwnerId ? ` · HubSpot owner ${row.person.hubspotOwnerId}` : ' · no HubSpot owner'}${row.person.salesManagerIds ? ` · SM ${row.person.salesManagerIds}` : ''}`}>
                            {row.person.fullName}
                        </span>
                        <span style={SMALL_MUTED}>{row.person.companyCode}</span>
                        {!row.person.isActive && <span style={SMALL_MUTED}>(left)</span>}
                        {!row.person.hasTargets && <span style={SMALL_MUTED} title="No person target stored for this year">no targets</span>}
                    </span>
                )}
                {row.kind === 'sum' && <span title="Σ over the persons of the node (personsActual / Σ person targets / achievement vs Σ)">Σ persons</span>}
                {row.kind === 'gtarget' && <span title="The stored group target and the achievement against it">Group target</span>}
            </td>
            {kpis.map(k => (
                <Cell
                    key={k.kpiKey}
                    kpi={k}
                    row={row}
                    cellMode={cellMode}
                    showLight={!yearEnd.has(k.kpiKey)}
                    scopeLabel={scope?.label ?? ''}
                    onClick={scope && cellClickable(row, k) ? () => onOpenEvidence(k.kpiKey, scope.scopeType, scope.scopeKey, scope.label, scope.partial) : undefined}
                />
            ))}
        </tr>
    );
}

function GroupLabel({ row }: { row: Extract<Row, { kind: 'group' }> }) {
    return (
        <>
            <span className="truncate">{row.group.label}</span>
            <span style={BADGE} title={`${SCOPE_LABEL[row.group.scopeType]} · ${row.group.personCount} persons in the node`}>
                {SCOPE_LABEL[row.group.scopeType]} · {row.personsHere < row.group.personCount ? `${row.personsHere} of ${row.group.personCount}` : row.group.personCount}
            </span>
            {row.group.partial && (
                <span style={BADGE_ATTN} title={`The selection keeps ${row.group.personCount} of the ${row.group.fullPersonCount} persons of this ${SCOPE_LABEL[row.group.scopeType].toLowerCase()}: the figures are over the selected persons, there is no group target, GP is the Σ of the persons and BAB has no figure.`}>
                    {row.group.personCount} of {row.group.fullPersonCount} selected
                </span>
            )}
            {row.spans && (
                <span style={BADGE_ATTN} title={`This team spans ${row.spans.join(', ')}; the figures are the whole team's (pooled by the engine), the members listed are those of this company.`}>
                    spans {row.spans.join(', ')}
                </span>
            )}
        </>
    );
}

const SCOPE_LABEL: Record<string, string> = { TOTAL: 'Total', BU: 'BU', COMPANY: 'Company', TEAM: 'Team', PERSON: 'Person', SELECTION: 'Selection' };

function rowScope(row: Row): { scopeType: EvidenceScopeType; scopeKey: string; label: string; partial?: boolean } | null {
    if (row.kind === 'hidden') return null;
    if (row.kind === 'person') return { scopeType: 'PERSON', scopeKey: row.person.staffId, label: row.person.fullName };
    if (row.group.scopeType === 'TOTAL') return null;
    return { scopeType: row.group.scopeType, scopeKey: row.group.scopeKey, label: `${row.group.label} (${SCOPE_LABEL[row.group.scopeType]})`, partial: row.group.partial };
}

/** BAB has rows only for a whole company / BU, and derived KPIs have no rows of their own — no click there. */
export function cellClickable(row: Row, kpi: KpiCatalogueEntry): boolean {
    if (row.kind === 'sum' || row.kind === 'gtarget' || row.kind === 'hidden') return false;
    if (kpi.groupAggregation === 'derived' || kpi.groupAggregation === 'mean') return false;
    if (kpi.category === 'bab' && (row.kind === 'person' || !hasBabRows(row.group))) return false;
    return true;
}

/**
 * Calm cell: the value and a small status dot — no cell background. The wall of coloured cells
 * hid the ranking (an 89 % and a 12 % looked the same); a dot beside the number keeps the status
 * readable without shouting, and the number itself carries it for colour-blind readers.
 *
 * K5 (S1-02): a cell with rows behind it holds a real <button> — reachable by Tab, opened with
 * Enter or Space, named "Gross Profit (EUR) — dsp (Company): 59 % · behind, rows".
 */
function Cell({ kpi, row, cellMode, showLight, scopeLabel, onClick }: { kpi: KpiCatalogueEntry; row: Row; cellMode: CellMode; showLight: boolean; scopeLabel: string; onClick?: () => void }) {
    const c = cellContent(kpi, row, cellMode);
    const light = showLight ? trafficLight(c.lightAchievement) : null;
    const content = (
        <>
            <span className="truncate" data-skpi-cell-text="">{c.text}</span>
            {c.noData && <Info size={12} className="inline shrink-0" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />}
            {light && <span className={LIGHT_DOT_CLASS[light]} data-light={light} aria-hidden="true" />}
        </>
    );
    if (!onClick) {
        return (
            <td className="n num" title={c.tooltip} data-kpi={kpi.kpiKey}>
                <span className="inline-flex max-w-full items-center justify-end gap-1.5">{content}</span>
            </td>
        );
    }
    return (
        <td className="n num" title={c.tooltip} data-kpi={kpi.kpiKey}>
            <button
                type="button"
                onClick={onClick}
                aria-label={`${kpiLabel(kpi)} — ${scopeLabel}: ${c.text || 'no value'}${light ? ` · ${STATUS_LABEL[light]}` : ''}, rows`}
                className="sort"
                style={{ width: '100%', justifyContent: 'flex-end', gap: 6, textDecoration: 'underline', textDecorationColor: 'transparent', textUnderlineOffset: 3 }}
            >
                {content}
            </button>
        </td>
    );
}

export interface CellContent { text: string; lightAchievement: number | null; tooltip: string; noData: boolean }

export type MatrixRow = Row;

export function cellContent(kpi: KpiCatalogueEntry, row: Row, mode: CellMode): CellContent {
    const unit = kpi.unit;
    const isVisits = kpi.category === 'visits';
    const ytdShare = judgedOnYtdShare(kpi);

    if (row.kind === 'hidden') return { text: '', lightAchievement: null, tooltip: '', noData: false };
    if (row.kind === 'person') {
        const v: KpiValue | undefined = row.person.values[kpi.kpiKey];
        if (!v) return { text: '—', lightAchievement: null, tooltip: 'No value', noData: true };
        const hasTarget = v.target !== null;
        const text = mode === 'actual' ? valueOrNoData(unit, v.actual, v.noData)
            : mode === 'achievement' ? formatAchievement(v.achievement, hasTarget)
            : mode === 'target' ? (hasTarget ? formatKpiValue(unit, v.target) : 'n/a')
            : formatKpiValue(unit, v.prior);
        return { text, lightAchievement: v.achievement, tooltip: describe(kpi, v, isVisits), noData: mode === 'actual' && v.actual === null };
    }

    const g: GroupKpiValue | undefined = row.group.values[kpi.kpiKey];
    if (!g) return { text: '—', lightAchievement: null, tooltip: 'No value', noData: true };
    const hasGroupTarget = g.target !== null;
    const hasPersonsTarget = g.personsTarget !== null;

    if (row.kind === 'sum') {
        const text = mode === 'actual' ? valueOrNoData(unit, g.personsActual, g.noData)
            : mode === 'achievement' ? formatAchievement(g.achievementVsPersons, hasPersonsTarget)
            : mode === 'target' ? (hasPersonsTarget ? formatKpiValue(unit, g.personsTarget) : 'n/a')
            : '—';
        return {
            text, lightAchievement: g.achievementVsPersons, noData: mode === 'actual' && g.personsActual === null,
            tooltip: `Σ persons: ${formatKpiValue(unit, g.personsActual)} · Σ person targets: ${hasPersonsTarget ? formatKpiValue(unit, g.personsTarget) : 'none'}${ytdShare && g.personsTargetYtd !== null ? ` (YTD ${formatKpiValue(unit, g.personsTargetYtd)})` : ''} · achievement vs Σ: ${formatAchievement(g.achievementVsPersons, hasPersonsTarget)}`,
        };
    }
    if (row.kind === 'gtarget') {
        const text = mode === 'achievement' ? formatAchievement(g.achievement, hasGroupTarget)
            : mode === 'prior' ? '—'
            : (hasGroupTarget ? formatKpiValue(unit, g.target) : 'n/a');
        return {
            text, lightAchievement: g.achievement, noData: false,
            tooltip: hasGroupTarget
                ? `Group target: ${formatKpiValue(unit, g.target)}${ytdShare && g.targetYtd !== null ? ` (YTD ${formatKpiValue(unit, g.targetYtd)})` : ''} · actual ${formatKpiValue(unit, g.actual)} · achievement ${formatAchievement(g.achievement, true)}`
                : 'No group target stored for this node',
        };
    }

    // group row. Average % is a mean of achievements: without a stored group target the engine's
    // group `actual` is a mean against nothing (0 %), and the figure that means something is the
    // mean vs the person targets — the same "no group target → vs Σ persons" rule as the light.
    const achievement = hasGroupTarget ? g.achievement : g.achievementVsPersons;
    const hasTarget = hasGroupTarget || hasPersonsTarget;
    const shownActual = kpi.groupAggregation === 'mean' && !hasGroupTarget ? g.personsActual : g.actual;
    const text = mode === 'actual' ? valueOrNoData(unit, shownActual, g.noData)
        : mode === 'achievement' ? formatAchievement(achievement, hasTarget)
        : mode === 'target' ? (hasGroupTarget ? formatKpiValue(unit, g.target) : hasPersonsTarget ? `Σ ${formatKpiValue(unit, g.personsTarget)}` : 'n/a')
        : formatKpiValue(unit, g.prior);
    return {
        text, lightAchievement: achievement, noData: mode === 'actual' && g.actual === null,
        tooltip: `${describe(kpi, g, isVisits)} · group target ${hasGroupTarget ? formatKpiValue(unit, g.target) : 'not set'} · Σ person targets ${hasPersonsTarget ? formatKpiValue(unit, g.personsTarget) : 'none'}${g.personsActual !== null && g.actual !== null && Math.abs(g.personsActual - g.actual) > 0.005 ? ` · Σ persons actual ${formatKpiValue(unit, g.personsActual)}` : ''}`,
    };
}

function valueOrNoData(unit: string, value: number | null, noData: string | undefined): string {
    return value === null ? (noData ? 'no data' : '—') : formatKpiValue(unit, value);
}

function describe(kpi: KpiCatalogueEntry, v: KpiValue, isVisits: boolean): string {
    const parts = [
        `${kpi.name}: ${v.actual === null ? `no data (${noDataText(v.noData)})` : formatKpiValue(kpi.unit, v.actual)}`,
        `target ${v.target === null ? 'none' : formatKpiValue(kpi.unit, v.target)}${judgedOnYtdShare(kpi) && v.targetYtd !== null ? ` (YTD ${formatKpiValue(kpi.unit, v.targetYtd)})` : ''}`,
        `achievement ${formatAchievement(v.achievement, v.target !== null)}`,
        `prior year ${formatKpiValue(kpi.unit, v.prior)}`,
    ];
    if (isVisits && v.planned !== undefined) parts.push(`${v.planned} planned to month end (not in the actual)`);
    if (kpi.direction === 'lower') parts.push('lower is better (achievement = target ÷ actual)');
    return parts.join(' · ');
}

// ---------------------------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------------------------

export interface Node {
    row: Row;
    children: Node[];
}

/** `babOnly`: BAB has figures per company / BU only, so teams and persons are left out. */
export function buildTree(data: ActualsResponse, hierarchy: Hierarchy, hidePersonsWithoutTargets: boolean, babOnly = false): Node[] {
    const groups = data.groups;
    const teamGroup = (staffId: string) => groups.find(g => g.scopeType === 'TEAM' && g.scopeKey === staffId) ?? null;

    const personNode = (p: PersonActuals, parentId: string, depth: number): Node =>
        ({ row: { kind: 'person', id: `${parentId}/p:${p.staffId}`, parentId, depth, person: p }, children: [] });

    /** The persons of a node; without any stored target they collapse into one line (when hiding is on). */
    const personNodes = (members: PersonActuals[], parentId: string, depth: number): Node[] => {
        if (babOnly) return [];
        if (!hidePersonsWithoutTargets) return members.map(p => personNode(p, parentId, depth));
        const nodes = members.filter(p => p.hasTargets).map(p => personNode(p, parentId, depth));
        const hidden = members.filter(p => !p.hasTargets).map(p => p.fullName).sort((a, b) => a.localeCompare(b));
        if (hidden.length > 0) nodes.push({ row: { kind: 'hidden', id: `${parentId}/hidden`, parentId, depth, names: hidden }, children: [] });
        return nodes;
    };

    const groupNode = (g: GroupActuals, parentId: string | null, depth: number, persons: PersonActuals[], childBuilder: (id: string) => Node[]): Node => {
        const id = `${parentId ?? ''}/${g.scopeType}:${g.scopeKey}`;
        const companiesHere = new Set(persons.map(p => p.companyCode));
        const spans = g.scopeType === 'TEAM' && hierarchy === 'company-team' && persons.length < g.personCount
            ? teamCompanies(data, g.scopeKey).filter(c => !companiesHere.has(c))
            : null;
        const children = childBuilder(id);
        // Σ persons and the stored group target side by side (decision 1) — only where a group
        // target exists; without one the node row already is the Σ-persons view.
        if (Object.values(g.values).some(v => v.target !== null)) {
            children.push({ row: { kind: 'sum', id: `${id}/sum`, parentId: id, depth: depth + 1, group: g }, children: [] });
            children.push({ row: { kind: 'gtarget', id: `${id}/gtarget`, parentId: id, depth: depth + 1, group: g }, children: [] });
        }
        return { row: { kind: 'group', id, parentId, depth, group: g, personsHere: persons.length, spans: spans && spans.length > 0 ? spans : null, hasChildren: children.length > 0 }, children };
    };

    /** Teams among `persons`, each holding the persons of that team; people without a manager go straight under the parent. */
    const teamNodes = (persons: PersonActuals[], parentId: string, depth: number): Node[] => {
        if (babOnly) return [];
        const byTeam = new Map<string, PersonActuals[]>();
        const orphans: PersonActuals[] = [];
        for (const p of persons) {
            if (!p.teamManagerStaffId) { orphans.push(p); continue; }
            const list = byTeam.get(p.teamManagerStaffId) ?? [];
            list.push(p);
            byTeam.set(p.teamManagerStaffId, list);
        }
        const nodes: Node[] = [];
        for (const [mgr, members] of byTeam) {
            const g = teamGroup(mgr) ?? syntheticTeam(mgr, members);
            nodes.push(groupNode(g, parentId, depth, members, id => personNodes(members, id, depth + 1)));
        }
        for (const p of orphans) nodes.push(personNode(p, parentId, depth));
        return nodes;
    };

    /** What sits under a BU (or under a single-BU selection): companies › teams, or teams. */
    const buChildren = (bu: string, persons: PersonActuals[], id: string, depth: number): Node[] => {
        // BAB has figures per company only: the companies always hang under the BU, whatever the hierarchy.
        if (hierarchy === 'team' && !babOnly) return teamNodes(persons, id, depth + 1);
        const companies = groups.filter(x => x.scopeType === 'COMPANY' && x.buCode === bu);
        return companies.map(c => {
            const members = persons.filter(p => p.companyCode === c.scopeKey);
            return groupNode(c, id, depth + 1, members, cid => teamNodes(members, cid, depth + 2));
        });
    };

    const buNode = (bu: string, parentId: string | null, depth: number): Node | null => {
        const g = groups.find(x => x.scopeType === 'BU' && x.scopeKey === bu);
        if (!g) return null;
        const persons = data.persons.filter(p => p.buCode === bu);
        return groupNode(g, parentId, depth, persons, id => buChildren(bu, persons, id, depth));
    };

    // K3: the selection is the root; under it the BUs (several) or directly the BU's contents.
    const selection = groups.find(g => g.scopeType === 'SELECTION');
    if (selection) {
        return [groupNode(selection, null, 0, data.persons, id => {
            if (data.bus.length > 1) return data.bus.map(b => buNode(b, id, 1)).filter((n): n is Node => n !== null);
            return buChildren(data.bus[0], data.persons, id, 0);
        })];
    }

    if (data.bus.length > 1) {
        const total = groups.find(g => g.scopeType === 'TOTAL');
        if (!total) return data.bus.map(b => buNode(b, null, 0)).filter((n): n is Node => n !== null);
        return [groupNode(total, null, 0, data.persons, id => data.bus.map(b => buNode(b, id, 1)).filter((n): n is Node => n !== null))];
    }
    const n = buNode(data.bus[0], null, 0);
    return n ? [n] : [];
}

function teamCompanies(data: ActualsResponse, managerStaffId: string): string[] {
    return [...new Set(data.persons.filter(p => p.teamManagerStaffId === managerStaffId).map(p => p.companyCode))].sort();
}

/** A manager the engine has no TEAM row for (should not happen; keeps the tree whole if it does). */
function syntheticTeam(mgr: string, members: PersonActuals[]): GroupActuals {
    return {
        scopeType: 'TEAM', scopeKey: mgr, label: members[0]?.teamManagerName ?? mgr, buCode: null, companyCode: null,
        personCount: members.length, hasTargets: false, values: {},
    };
}

function defaultExpanded(data: ActualsResponse): Set<string> {
    const s = new Set<string>();
    const selection = data.groups.find(g => g.scopeType === 'SELECTION');
    if (selection) {
        const root = `/SELECTION:${selection.scopeKey}`;
        s.add(root);
        if (data.bus.length > 1) for (const b of data.bus) s.add(`${root}/BU:${b}`);
        return s;
    }
    if (data.bus.length > 1) {
        const total = data.groups.find(g => g.scopeType === 'TOTAL');
        if (total) s.add(`/TOTAL:${total.scopeKey}`);
        for (const b of data.bus) s.add(`${total ? `/TOTAL:${total.scopeKey}` : ''}/BU:${b}`);
    } else {
        s.add(`/BU:${data.bus[0]}`);
    }
    return s;
}

function allGroupIds(nodes: Node[]): string[] {
    const out: string[] = [];
    const walk = (n: Node) => { if (n.row.kind === 'group') out.push(n.row.id); n.children.forEach(walk); };
    nodes.forEach(walk);
    return out;
}

function sortValue(row: Row, key: string, mode: CellMode): string | number | null {
    if (row.kind === 'hidden') return null;
    if (key === NAME_KEY) return row.kind === 'person' ? row.person.fullName : row.group.label;
    if (row.kind === 'person') {
        const v = row.person.values[key];
        if (!v) return null;
        return mode === 'achievement' ? v.achievement : mode === 'target' ? v.target : mode === 'prior' ? v.prior : v.actual;
    }
    const g = row.group.values[key];
    if (!g) return null;
    return mode === 'achievement' ? (g.target !== null ? g.achievement : g.achievementVsPersons)
        : mode === 'target' ? (g.target ?? g.personsTarget) : mode === 'prior' ? g.prior : g.actual;
}

export function flatten(nodes: Node[], expanded: Set<string>, sort: SortState<string>, mode: CellMode): Row[] {
    const out: Row[] = [];
    const cmp = (a: Node, b: Node) => {
        // Σ / group-target rows stay at the bottom of their node, in that order.
        const rank = (n: Node) => n.row.kind === 'hidden' ? 1 : n.row.kind === 'sum' ? 2 : n.row.kind === 'gtarget' ? 3 : 0;
        if (rank(a) !== rank(b)) return rank(a) - rank(b);
        if (!sort.key) return 0;
        const av = sortValue(a.row, sort.key, mode);
        const bv = sortValue(b.row, sort.key, mode);
        const aNil = av === null || av === undefined || av === '';
        const bNil = bv === null || bv === undefined || bv === '';
        if (aNil && bNil) return 0;
        if (aNil) return 1;
        if (bNil) return -1;
        const base = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return base * (sort.direction === 'asc' ? 1 : -1);
    };
    const walk = (n: Node) => {
        out.push(n.row);
        if (n.row.kind === 'group' && expanded.has(n.row.id)) [...n.children].sort(cmp).forEach(walk);
    };
    [...nodes].sort(cmp).forEach(walk);
    return out;
}
